package com.devi3east.bashbuddy.dashboard.ui.components.dialogs

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp

@Composable
fun AdjustTicketDialog(
    ticketTitle: String,
    maxQuantity: Int,
    availableQuantity: Int,
    ticketsSold: Int,
    onDismiss: () -> Unit,
    onSubmit: (adjustBy: Int) -> Unit
) {
    var adjustAmount by remember { mutableStateOf("") }
    var adjustType by remember { mutableStateOf("Increase") }
    var errorText by remember { mutableStateOf<String?>(null) }

    fun validateInput(): Boolean {
        val value = adjustAmount.toIntOrNull() ?: return true
        if (adjustType == "Decrease" && value > availableQuantity) {
            errorText = "Cannot decrease more than available tickets ($availableQuantity)"
            return false
        }
        errorText = null
        return true
    }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Text(
                "Adjust Ticket Availability",
                modifier = Modifier.fillMaxWidth(),
                textAlign = TextAlign.Center,
                style = MaterialTheme.typography.titleLarge.copy(
                    fontWeight = FontWeight.SemiBold
                )
            )
        },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(16.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                    Text("Ticket: $ticketTitle")
                    Text("Max Quantity: $maxQuantity")
                    Text("Tickets Sold: $ticketsSold")
                    Text("Currently Available: $availableQuantity")
                }

                Row(
                    horizontalArrangement = Arrangement.Center,
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    ToggleButton(
                        text = "Increase",
                        selected = adjustType == "Increase",
                        onClick = { adjustType = "Increase"; validateInput() }
                    )
                    ToggleButton(
                        text = "Decrease",
                        selected = adjustType == "Decrease",
                        onClick = { adjustType = "Decrease"; validateInput() }
                    )
                }

                OutlinedTextField(
                    value = adjustAmount,
                    onValueChange = {
                        adjustAmount = it
                        validateInput()
                    },
                    label = { Text("Amount") },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    isError = errorText != null,
                    shape = RoundedCornerShape(12.dp)
                )

                errorText?.let {
                    Text(
                        it,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.labelMedium,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    val amount = adjustAmount.toIntOrNull()
                    if (amount != null && errorText == null) {
                        val finalAmount = if (adjustType == "Increase") amount else -amount
                        onSubmit(finalAmount)
                    }
                },
                enabled = adjustAmount.isNotEmpty() && errorText == null,
                colors = ButtonDefaults.buttonColors(
                    containerColor = MaterialTheme.colorScheme.primary,
                    contentColor = MaterialTheme.colorScheme.onPrimary
                ),
                shape = RoundedCornerShape(12.dp)
            ) {
                Text("Save")
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancel", color = MaterialTheme.colorScheme.onSurfaceVariant)
            }
        },
        shape = RoundedCornerShape(24.dp),
        containerColor = MaterialTheme.colorScheme.surface,
        tonalElevation = 6.dp
    )
}

@Composable
private fun ToggleButton(
    text: String,
    selected: Boolean,
    onClick: () -> Unit
) {
    val backgroundColor =
        if (selected) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.surfaceVariant
    val contentColor =
        if (selected) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onSurface

    Button(
        onClick = onClick,
        colors = ButtonDefaults.buttonColors(
            containerColor = backgroundColor,
            contentColor = contentColor
        ),
        modifier = Modifier
            .width(120.dp)
            .height(40.dp)
            .padding(horizontal = 4.dp),
        shape = RoundedCornerShape(12.dp),
        elevation = ButtonDefaults.buttonElevation(defaultElevation = 2.dp)
    ) {
        Text(text)
    }
}